import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Repeat } from 'lucide-react'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { Icon } from '../../platform/icons/Icon'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { accountLabel } from '../accounts/accountLabel'
import { useAccounts } from '../accounts/useAccounts'
import { BillRowActions, LifecycleConfirm, type PendingConfirm } from './BillLifecycle'
import { AmountChangeShownScope } from './recurringAnalytics'
import { RecurringBackLink } from './BillChrome'
import { ChoosePaymentSheet } from './ChoosePaymentSheet'
import { PASSBOOK_ORDER, passbookSections, reconnectBillCount } from './listSections'
import { ReconnectBanner } from './ReconnectBanner'
import { RECURRING_NEW_PATH, RECURRING_SUGGESTIONS_PATH } from './recurringPaths'
import { SectionBlock, sublineOf, type AccountNames } from './RecurringSections'
import { canMarkPaid, formatDue, localToday, passbookSection, type LocalDate, type PassbookKey } from './recurringRules'
import { frequencyLabel, type Bill, type Entry } from './recurringWire'
import { useAmountChanges } from './useAmountChanges'
import { liveBill, useBills } from './useRecurring'
import './recurring.css'

const TITLES: Record<PassbookKey, string> = {
  overdue: 'Overdue',
  upcoming: 'Upcoming',
  paid: 'Paid this month',
  paused: 'Paused',
  income: 'Income',
}

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)

/** The row's second line per section. A stream with no next due date shows no date at all. */
function subline(e: Entry, k: PassbookKey, today: LocalDate, accounts: AccountNames): string {
  const { bill } = e
  const account = bill.accountId ? accounts.get(bill.accountId) : undefined
  const freq = lowerFirst(frequencyLabel(bill.frequency))
  const join = (parts: (string | undefined)[]) => parts.filter((p): p is string => p !== undefined).join(' · ')
  if (k === 'paused' || bill.nextDue === null) return join([freq, account])
  if (k === 'paid') return join([`Paid ${formatDue(bill.lastDate ?? e.dueDate, 'short')}`, `next ${formatDue(bill.nextDue, 'short')}`])
  return sublineOf(e, k === 'overdue' ? 'overdue' : 'upcoming', today, accounts)
}

function Skeletons() {
  return (
    <Panel className="rec-sections">
      <div className="rec-section__band">
        <Skeleton height={10} width={90} />
        <Skeleton height={10} width={70} />
      </div>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="rec-skeleton-row">
          <Skeleton height={34} width={34} />
          <span className="rec-skeleton-row__text">
            <Skeleton height={13} width="30%" />
            <Skeleton height={10} width="45%" />
          </span>
          <Skeleton height={13} width={70} />
        </div>
      ))}
    </Panel>
  )
}

const LOAD_ERROR = 'Recurring payments could not be loaded.'

/**
 * Cancelled-not-removed and removed bills appear nowhere, so a household with
 * only those is empty. The surface is live, so `stale` never arrives here; a
 * stale input passes through untouched and `<Async>` refuses it (P1).
 */
function visibleState(bills: AsyncState<Bill[]>, today: LocalDate): AsyncState<Bill[]> {
  if (bills.status === 'error') return { ...bills, message: LOAD_ERROR }
  if (bills.status !== 'success') return bills
  const shown = bills.data.filter((b) => passbookSection(b, today) !== null)
  return shown.length === 0 ? { status: 'empty' } : { ...bills, data: shown }
}

/**
 * `/recurring/all` — the Passbook: one row per stream (S5), in Overdue,
 * Upcoming, Paid this month, Paused and Income, each absent when empty (R4).
 * Section totals are per currency and never summed (S12); Income shows a count
 * because inflows are excluded from every total (D6). A paused stream's home is
 * here (S10), with Resume in its actions. No tiles: the month tiles' names
 * would mean something else here. Rows render through the month list's own
 * `SectionBlock`.
 */
export function AllRecurring() {
  const today = localToday()
  const phone = useBreakpoint() === 'phone'
  const navigate = useNavigate()
  const [paying, setPaying] = useState<Bill | null>(null)
  const [confirming, setConfirming] = useState<PendingConfirm | null>(null)
  const billsState = useBills()
  const state = useMemo(() => visibleState(billsState, today), [billsState, today])
  const accountsState = useAccounts()
  const accounts: AccountNames = useMemo(
    () =>
      new Map(
        accountsState.status === 'success' || accountsState.status === 'stale'
          ? accountsState.data.map((a) => [a.id, accountLabel(a)] as const)
          : [],
      ),
    [accountsState],
  )
  const shown = state.status === 'success' ? state.data : null
  const sections = useMemo(() => (shown ? passbookSections(shown, today) : null), [shown, today])
  const changes = useAmountChanges(shown ? shown.map((b) => b.id) : [])

  const active = sections ? sections.overdue.entries.length + sections.upcoming.entries.length + sections.paid.entries.length : 0
  const paused = sections ? sections.paused.entries.length : 0

  const actionsFor = (k: PassbookKey) => (e: Entry) => (
    <BillRowActions
      bill={e.bill}
      entry={e}
      inline={!phone && k === 'overdue' && canMarkPaid(e.bill, e)}
      onOpenSheet={() => setPaying(e.bill)}
      onConfirm={setConfirming}
    />
  )

  return (
    <AmountChangeShownScope>
      <div className="rec-page">
        <RecurringBackLink label="Recurring payments" />
        <div className="rec-head">
          <div className="rec-head__identity">
            <h2 className="rec-all__title">All recurring</h2>
            {sections && (
              <p className="rec-head__summary" data-testid="rec-summary">
                {active} active{paused > 0 ? ` · ${paused} paused` : ''}
              </p>
            )}
          </div>
          <div className="rec-head__actions">
            <ButtonLink to={RECURRING_NEW_PATH} variant="primary" size="page">
              + Add recurring
            </ButtonLink>
          </div>
        </div>
        <Async
          surface="recurring-all"
          state={state}
          skeleton={<Skeletons />}
          empty={{
            icon: <Icon glyph={Repeat} size={24} />,
            title: 'No recurring payments yet',
            body: "We'll spot repeating charges for you as transactions come in.",
            action: { label: 'Look for recurring payments', onAct: () => navigate(RECURRING_SUGGESTIONS_PATH) },
          }}
          announce={(d) => `${d.length} recurring ${d.length === 1 ? 'payment' : 'payments'}`}
        >
          {() => {
            // Children render on success only, so `sections` is set.
            const s = sections!
            const reconnect = reconnectBillCount(PASSBOOK_ORDER.flatMap((k) => s[k].entries))
            return (
              <div className="rec-body">
                {reconnect > 0 && (
                  <ReconnectBanner
                    text={reconnect === 1 ? "1 bill can't be confirmed." : `${reconnect} bills can't be confirmed.`}
                  />
                )}
                <Panel className="rec-sections">
                  {PASSBOOK_ORDER.filter((k) => s[k].entries.length > 0).map((k) => {
                    const n = s[k].entries.length
                    return (
                      <SectionBlock
                        key={k}
                        title={TITLES[k]}
                        entries={s[k].entries}
                        totals={s[k].totals}
                        totalSlot={k === 'income' ? (n === 1 ? '1 stream' : `${n} streams`) : undefined}
                        overdue={k === 'overdue'}
                        muted={k === 'paid'}
                        rowKey={(e) => e.bill.id}
                        subline={(e) => subline(e, k, today, accounts)}
                        layout={phone ? 'cards' : 'grid'}
                        changes={changes}
                        actions={actionsFor(k)}
                      />
                    )
                  })}
                </Panel>
              </div>
            )
          }}
        </Async>
        {paying && <ChoosePaymentSheet bill={liveBill(paying, billsState)} onClose={() => setPaying(null)} />}
        {confirming && <LifecycleConfirm confirm={confirming} onClose={() => setConfirming(null)} />}
      </div>
    </AmountChangeShownScope>
  )
}
